import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Button,
  Box,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ICourse } from "../types/courses-list";
import apiService from "../../../service/Api";
import { useNavigate } from "react-router-dom";
import { AddCircleOutlineOutlined } from "@mui/icons-material";

interface CourseResponse {
  results: ICourse[];
  count: number;
}

export default function CourseManagement() {
  const [courses, setCourses] = useState<ICourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<CourseResponse>(
        "/api/lms/courses/"
      );
      setCourses(response.data.results);
    } catch (err) {
      console.error(err);
      setError("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleView = (courseId: string) => {
    navigate(`/courses/${courseId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          startIcon={<AddCircleOutlineOutlined />}
          onClick={() => {
            navigate("/courses/create-course");
          }}
          sx={{ backgroundColor: "#614438", color: "white", mb: 2 }}
        >
          Add Courses
        </Button>
      </Box>
      <Grid container spacing={3}>
        {courses.map((course) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={course.id}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                boxShadow: "none",
                transition: "box-shadow 0.2s ease",
                "&:hover": {
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                },
              }}
            >
              {/* Thumbnail */}
              <Box
                sx={{
                  height: 160,
                  backgroundColor: "#f3f4f6",
                  backgroundImage: course.image
                    ? `url(${course.image})`
                    : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!course.image && (
                  <Typography sx={{ color: "text.secondary" }}>
                    No Image
                  </Typography>
                )}
              </Box>

              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                {/* Status + Type */}
                <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <Chip
                    size="small"
                    label={course.type}
                    sx={{
                      backgroundColor: "#eef2ff",
                      color: "#3730a3",
                      fontWeight: 500,
                      fontSize: "0.7rem",
                    }}
                  />

                  {course.status === "published" && (
                    <Chip
                      size="small"
                      label="Published"
                      sx={{
                        backgroundColor: "#dcfce7",
                        color: "#166534",
                        fontWeight: 500,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                  noWrap
                >
                  {course.title}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: "text.secondary",
                    mb: 2,
                  }}
                  noWrap
                >
                  {course.description}
                </Typography>

                {/* Meta */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <VisibilityIcon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      {course.views}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>

              <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleView(course.id)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: 2,
                  }}
                >
                  View Content
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
